import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ModalController } from '@ionic/angular';

import {
  StateService,
  RouterService,
  ToolService,
  ConstructionDayReportType,
  ViewerComponent as viewerComp,
  NodeItem,
} from 'projects/shared/src/public-api';
import { PdfType } from '../models/pdf-type';
import { ModalRole } from '../models/modal-roles';
import { Subscription } from 'rxjs';

@Component({
  selector: 'lib-viewer-dialog',
  templateUrl: './viewer-dialog.component.html',
  styleUrls: ['./viewer-dialog.component.scss'],
  standalone: false,
})
export class ViewerDialogComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);

  fileExtension: string;
  docIds: string[] = [];
  currentNodeItemId: string;
  pdfType: string;
  name: string;
  docLoaded: boolean;
  isPdf: boolean;
  @ViewChild(viewerComp) child!: viewerComp;
  currentIndex: number = 0;
  private nodeItems: NodeItem[] = [];
  private subs: Subscription[] = [];

  constructor(
    public state: StateService,
    private modalController: ModalController,
    public routerService: RouterService,
    public tools: ToolService
  ) {}

  ngOnInit() {
    if (this.pdfType === PdfType.Document) {
      this.subs.push(
        this.state.nodeItems$.subscribe((x) => {
          this.nodeItems = x;
          this.removeFolders();
          this.initDoc();
        })
      );
    } else if (this.pdfType === PdfType.ConstructionDayReport) {
      this.state
        .loadConSiteReportById(this.docIds[this.currentIndex])
        .subscribe((x) => {
          let typeLabel = '';
          switch (x.type) {
            case ConstructionDayReportType.$0:
              typeLabel = this.translate.instant('reports.daily');
              break;
            case ConstructionDayReportType.$1:
              typeLabel = this.translate.instant('reports.trip');
              break;
            case ConstructionDayReportType.$2:
              typeLabel = this.translate.instant('reports.work');
              break;
          }

          this.name =
            typeLabel +
            ' ' +
            this.state.getConstructionSiteName(x.constructionSite) +
            this.translate.instant('common.fromDate', {
              date: this.tools.formatDt(x.date),
            });
          this.fileExtension = '.pdf';
          this.currentNodeItemId = x.id;
          this.docLoaded = true;
        });
    } else if (this.pdfType === PdfType.HourOverview) {
      this.name = this.translate.instant('hours.monthlyOverviewAsOf', {
        date: this.tools.formatDt(new Date().toISOString()),
      });
      this.fileExtension = '.pdf';
      this.currentNodeItemId = this.docIds[this.currentIndex];
      this.docLoaded = true;
    } else if (this.pdfType === PdfType.Inspection) {
      this.name = this.translate.instant('inspection.report');
      this.fileExtension = '.pdf';
      this.currentNodeItemId = this.docIds[this.currentIndex];
      this.docLoaded = true;
    }
  }

  ngOnDestroy() {
    this.subs.forEach((x) => x?.unsubscribe());
  }

  private removeFolders(): void {
    const docId = this.docIds[this.currentIndex];
    this.docIds = this.docIds.filter((id) => {
      const item = this.nodeItems.find((y) => y.id === id);
      return item?.fileExtension != null;
    });
    this.currentIndex = this.docIds.indexOf(docId);
  }

  private initDoc() {
    let nodeItem = this.nodeItems.find(
      (x) => x.id == this.docIds[this.currentIndex]
    );
    this.name = nodeItem.displayName;
    this.fileExtension = nodeItem.fileExtension;
    this.isPdf = nodeItem.fileExtension == '.pdf';
    this.currentNodeItemId = nodeItem.id;
    if (this.docLoaded)
      this.child.loadFile(
        this.currentNodeItemId,
        this.pdfType,
        this.fileExtension,
        this.name
      );
    this.docLoaded = true;
  }

  public cancel() {
    return this.modalController.dismiss(null, ModalRole.Cancel);
  }

  public async download() {
    await this.child.downloadFile();
  }

  public async changeViewer() {
    await this.child.changeViewer();
  }

  public previousImage() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.initDoc();
    }
  }

  public nextImage() {
    if (this.currentIndex < this.docIds.length - 1) {
      this.currentIndex++;
      this.initDoc();
    }
  }

  public hasPreviousImage(): boolean {
    return this.currentIndex > 0;
  }

  public hasNextImage(): boolean {
    return this.currentIndex < this.docIds.length - 1;
  }
}
